import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Eye, Edit, Trash2, UserPlus, Loader2, X } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [partners, setPartners] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const addTriggered = useRef(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    dob: '',
    ssn: '',
    card_number: '',
    partner_id: '',
    referral_name: '',
    payment_plan: 'Month',
    monthly_payment_amount: 0,
    number_of_installments: 1,
    contract_value: 0,
    installments: 1
  });

  const canManageClients = ['aline', 'administrative', 'operator', 'office_admin'].includes(user?.role);
  const canSeeSensitive = ['aline', 'administrative'].includes(user?.role);

  useEffect(() => {
    fetchClients();
    if (canManageClients) {
      fetchPartners();
    }
  }, []);

  // Read status filter and action from URL query params
  useEffect(() => {
    const urlStatus = searchParams.get('status');
    if (urlStatus) {
      setStatusFilter(urlStatus);
    }
    // Auto-open Add Client dialog when ?action=add
    if (searchParams.get('action') === 'add' && !addTriggered.current) {
      addTriggered.current = true;
      setShowAddDialog(true);
      // Clean URL without re-triggering
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      window.history.replaceState({}, '', `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`);
    }
  }, [searchParams]);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/clients`);
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/partners`);
      setPartners(data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/clients`, formData);
      setShowAddDialog(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        dob: '',
        ssn: '',
        card_number: '',
        partner_id: '',
        referral_name: '',
        payment_plan: 'Month',
        monthly_payment_amount: 0,
        number_of_installments: 1,
        contract_value: 0,
        installments: 1
      });
      fetchClients();
      alert(`Client created!\n\nCode: ${res.data.client_code}\nLogin Email: ${res.data.login_email}\nPassword: manager0100@\nRole: client`);
    } catch (error) {
      console.error('Error adding client:', error);
      alert(error.response?.data?.detail || 'Error adding client');
    } finally {
      setFormLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch =
      `${client.first_name} ${client.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.client_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.referral_name || '').toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    if (statusFilter === 'active') {
      matchesStatus = client.status === 'Active Client' || client.status === 'active';
    } else if (statusFilter === 'collection') {
      matchesStatus = client.status === 'Collection Attention';
    } else if (statusFilter === 'pending') {
      matchesStatus = client.payment_status === 'Pending' || client.payment_status === 'Overdue';
    }

    return matchesSearch && matchesStatus;
  });

  const clearStatusFilter = () => {
    setStatusFilter('all');
    setSearchParams({});
  };

  const statusFilterLabel = {
    active: 'Active Clients',
    collection: 'Collection Attention',
    pending: 'Pending Payment',
    all: null
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Clients</h1>
            <p className="text-slate-500 mt-1">Manage all client records</p>
          </div>
          {canManageClients && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-client-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Client</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddClient} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">First Name</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                        data-testid="input-first-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">Last Name</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                        data-testid="input-last-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="font-semibold">Client Email (used as login) *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        placeholder="client@email.com"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        data-testid="input-address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="dob">Date of Birth</Label>
                      <Input
                        id="dob"
                        type="date"
                        value={formData.dob}
                        onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                        data-testid="input-dob"
                      />
                    </div>
                    {canSeeSensitive && (
                      <>
                        <div>
                          <Label htmlFor="ssn">SSN</Label>
                          <Input
                            id="ssn"
                            value={formData.ssn}
                            onChange={(e) => setFormData({ ...formData, ssn: e.target.value })}
                            placeholder="XXX-XX-XXXX"
                            data-testid="input-ssn"
                          />
                        </div>
                        <div>
                          <Label htmlFor="card_number">Card Number</Label>
                          <Input
                            id="card_number"
                            value={formData.card_number}
                            onChange={(e) => setFormData({ ...formData, card_number: e.target.value })}
                            placeholder="XXXX-XXXX-XXXX-XXXX"
                            data-testid="input-card"
                          />
                        </div>
                      </>
                    )}
                    <div>
                      <Label htmlFor="partner_id">Referral Partner</Label>
                      <Select
                        value={formData.partner_id}
                        onValueChange={(value) => setFormData({ ...formData, partner_id: value })}
                      >
                        <SelectTrigger data-testid="select-partner">
                          <SelectValue placeholder="Select partner (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Partner</SelectItem>
                          {partners.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="referral_name">Referral Name</Label>
                      <Input
                        id="referral_name"
                        value={formData.referral_name}
                        onChange={(e) => setFormData({ ...formData, referral_name: e.target.value })}
                        placeholder="Referral person name"
                        data-testid="input-referral-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="payment_plan">Payment Plan</Label>
                      <Select
                        value={formData.payment_plan}
                        onValueChange={(value) => setFormData({ ...formData, payment_plan: value })}
                      >
                        <SelectTrigger data-testid="select-payment-plan">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Month">Monthly</SelectItem>
                          <SelectItem value="Bi-Weekly">Bi-Weekly</SelectItem>
                          <SelectItem value="Weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="monthly_payment_amount">Monthly Payment ($)</Label>
                      <Input
                        id="monthly_payment_amount"
                        type="number"
                        value={formData.monthly_payment_amount}
                        onChange={(e) => setFormData({ ...formData, monthly_payment_amount: parseFloat(e.target.value) || 0 })}
                        data-testid="input-monthly-payment"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contract_value">Contract Value ($)</Label>
                      <Input
                        id="contract_value"
                        type="number"
                        value={formData.contract_value}
                        onChange={(e) => setFormData({ ...formData, contract_value: parseFloat(e.target.value) || 0 })}
                        data-testid="input-contract-value"
                      />
                    </div>
                    <div>
                      <Label htmlFor="installments">Number of Installments</Label>
                      <Input
                        id="installments"
                        type="number"
                        min="1"
                        value={formData.installments}
                        onChange={(e) => setFormData({ ...formData, installments: parseInt(e.target.value) || 1 })}
                        data-testid="input-installments"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={formLoading} data-testid="submit-add-client">
                      {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Add Client
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search clients by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-clients"
              />
            </div>
            {/* Active Status Filter Badge */}
            {statusFilter !== 'all' && statusFilterLabel[statusFilter] && (
              <div className="flex items-center gap-2 mt-3" data-testid="active-filter">
                <span className="text-sm text-slate-500">Filtered by:</span>
                <Badge className="bg-[#1a2e5a] text-white text-xs px-3 py-1 flex items-center gap-1.5">
                  {statusFilterLabel[statusFilter]}
                  <button onClick={clearStatusFilter} className="hover:opacity-75" data-testid="clear-filter-btn">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clients Table */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="clients-table">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Code</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Name</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Contact</TableHead>
                  {canSeeSensitive && (
                    <TableHead className="text-xs font-medium text-slate-500 uppercase">SSN</TableHead>
                  )}
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Referral</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Payment</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Created Date</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Status</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-slate-500 py-8">
                      No clients found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client) => (
                    <TableRow
                      key={client.id}
                      className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/clients/${client.id}`)}
                      data-testid={`client-row-${client.id}`}
                    >
                      <TableCell>
                        <span className="font-mono text-sm text-blue-600 font-medium">{client.client_code || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{client.first_name} {client.last_name}</p>
                          <p className="text-sm text-slate-500">{client.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{client.phone}</TableCell>
                      {canSeeSensitive && (
                        <TableCell className="font-mono text-slate-600">
                          {client.ssn || client.ssn_masked || '***-**-****'}
                        </TableCell>
                      )}
                      <TableCell className="text-slate-600 text-sm">
                        {client.referral_name || '-'}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-slate-900">{client.monthly_payment_amount ? `$${client.monthly_payment_amount}/mo` : '-'}</p>
                          <p className="text-sm text-slate-500">{client.number_of_installments ? `${client.number_of_installments}x` : ''}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {client.created_at ? format(new Date(client.created_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          client.status === 'Active Client' || client.status === 'active' 
                            ? 'bg-emerald-100 text-emerald-800' 
                            : client.status === 'Collection Attention'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-slate-100 text-slate-800'
                        }>
                          {client.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/admin/clients/${client.id}`); }}
                          data-testid={`view-client-${client.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
